import { Controller } from "@hotwired/stimulus";

// Two-step land filtering: choose exactly one indexed field, then provide a
// value using the control suited to that field. Inactive panels stay disabled
// so the existing attribute/operator/value query contract remains unchanged.
export default class extends Controller {
  static targets = [
    "attribute",
    "clearFilter",
    "label",
    "menu",
    "panel",
    "picker",
    "toggle",
    "valueSection",
  ];

  connect() {
    this.syncPanels();
  }

  disconnect() {
    window.clearTimeout(this.submitTimer);
  }

  toggle() {
    const opening = this.menuTarget.classList.contains("hidden");
    this.menuTarget.classList.toggle("hidden", !opening);
    this.toggleTarget.setAttribute("aria-expanded", String(opening));
  }

  close() {
    this.menuTarget.classList.add("hidden");
    this.toggleTarget.setAttribute("aria-expanded", "false");
  }

  closeOnOutside(event) {
    if (!this.pickerTarget.contains(event.target)) this.close();
  }

  selectField(event) {
    const { attribute, label } = event.params;
    const fieldChanged = this.attributeTarget.value !== attribute;
    const hadAppliedValue = this.activePanelHasValue;

    if (fieldChanged) this.clearPanelValues();

    this.attributeTarget.value = attribute;
    this.labelTarget.textContent = label;
    this.valueSectionTarget.classList.remove("hidden");
    this.syncPanels();
    this.close();

    if (fieldChanged && hadAppliedValue) this.clearFilterTarget.click();

    requestAnimationFrame(() => {
      this.activePanel?.querySelector("input:not([type='hidden'])")?.focus();
    });
  }

  clearPanelValues() {
    this.panelTargets.forEach((panel) => {
      panel
        .querySelectorAll('[data-controller~="date-picker"]')
        .forEach((element) => {
          const controller = this.application.getControllerForElementAndIdentifier(
            element,
            "date-picker",
          );
          controller?.datepickerInstance?.clear({ silent: true });
        });

      panel.querySelectorAll("input, select, textarea").forEach((control) => {
        if (control.type === "radio" || control.type === "checkbox") {
          control.checked = false;
        } else if (control.tagName === "SELECT") {
          control.selectedIndex = 0;
        } else {
          control.value = "";
        }
      });
    });
  }

  syncPanels() {
    const selected = this.attributeTarget.value;

    this.panelTargets.forEach((panel) => {
      const active = panel.dataset.attribute === selected;
      panel.classList.toggle("hidden", !active);
      panel.querySelectorAll("input, select, textarea").forEach((control) => {
        control.disabled = !active;
        control.required = active && control.dataset.optional !== "true";
      });
    });

    const operators = this.activePanel?.querySelectorAll(
      'input[name="operator"]',
    );
    if (operators?.length && !Array.from(operators).some(({ checked }) => checked)) {
      operators[0].checked = true;
    }
  }

  debounceSubmit(event) {
    window.clearTimeout(this.submitTimer);
    if (!event.currentTarget.value.trim()) return;

    this.submitTimer = window.setTimeout(() => this.submitNow(), 300);
  }

  submitNow() {
    window.clearTimeout(this.submitTimer);
    if (this.form.checkValidity()) this.form.requestSubmit();
  }

  get form() {
    return this.element.querySelector("form");
  }

  get activePanel() {
    return this.panelTargets.find(
      (panel) => panel.dataset.attribute === this.attributeTarget.value,
    );
  }

  get activePanelHasValue() {
    return Array.from(
      this.activePanel?.querySelectorAll('[name="value"]') || [],
    ).some((control) =>
      control.type === "radio" || control.type === "checkbox"
        ? control.checked
        : control.value.trim() !== "",
    );
  }
}
